import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    fetchPublisherSummary,
    fetchPublisherByDay,
    fetchPublisherByAdUnit,
    fetchPublisherByFormat,
    exportPublisherReport,
    fetchAdInventory
} from '../../shared/utils/api';
import { useIsMounted } from '../../shared/hooks';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { MetricCard, ChartContainer } from '@bafkal/ui-shared/charts';
import { ErrorMessage, Table, Button } from '@bafkal/ui-shared/components';
import { Select } from '@bafkal/ui-shared/forms';
import { extractContractError } from '@bafkal/ui-shared/utils';
import { colors } from '@bafkal/ui-shared/tokens';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const getTimeRangeDates = (range) => {
    const to = new Date();
    const from = new Date();
    if (range === '30d') {
        from.setDate(to.getDate() - 30);
    } else if (range === '90d') {
        from.setDate(to.getDate() - 90);
    } else {
        // default 7d
        from.setDate(to.getDate() - 7);
    }
    const formatDate = (d) => d.toISOString().slice(0, 10);
    return { from: formatDate(from), to: formatDate(to) };
};

const formatMicrodollars = (microdollars) => {
    if (microdollars == null) return '—';
    const dollars = Number(microdollars) / 1000000;
    return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatCurrency = (val) => {
    if (val == null) return '—';
    return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (val) => {
    if (val == null) return '—';
    return `${(Number(val) * 100).toFixed(1)}%`;
};

const Dashboard = () => {
    const [summary, setSummary] = useState(null);
    const [dailyData, setDailyData] = useState([]);
    const [adUnitReport, setAdUnitReport] = useState({ items: [], total: 0, page: 1, pageSize: 20 });
    const [formatData, setFormatData] = useState([]);
    const [adUnits, setAdUnits] = useState([]);
    const [selectedAdUnit, setSelectedAdUnit] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d
    const [sortBy, setSortBy] = useState('impressions');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [isExporting, setIsExporting] = useState(false);

    const isMounted = useIsMounted();

    useEffect(() => {
        const loadInventory = async () => {
            try {
                const units = await fetchAdInventory();
                if (isMounted.current) {
                    setAdUnits(units.items || []);
                }
            } catch (err) {
                console.error('Failed to load ad inventory', err);
            }
        };
        loadInventory();
    }, [isMounted]);

    const loadReporting = useCallback(async () => {
        if (isMounted.current) setLoading(true);
        const { from, to } = getTimeRangeDates(timeRange);

        try {
            const [summaryRes, dailyRes, adUnitRes, formatRes] = await Promise.allSettled([
                fetchPublisherSummary(from, to),
                fetchPublisherByDay(from, to, 1, 90),
                fetchPublisherByAdUnit(from, to, 1, 100, sortBy, sortDir),
                fetchPublisherByFormat(from, to, 1, 20)
            ]);

            if (!isMounted.current) return;

            if (summaryRes.status === 'fulfilled') {
                setSummary(summaryRes.value);
                setError(null);
            } else if (summaryRes.reason?.response?.status === 404) {
                setSummary(null);
                setError(null);
            } else {
                const contractError = extractContractError(summaryRes.reason, 'Failed to load reporting summary');
                setError(contractError.message);
            }

            if (dailyRes.status === 'fulfilled') {
                setDailyData(dailyRes.value?.items || []);
            }
            if (adUnitRes.status === 'fulfilled') {
                setAdUnitReport(adUnitRes.value || { items: [], total: 0, page: 1, pageSize: 20 });
            }
            if (formatRes.status === 'fulfilled') {
                setFormatData(formatRes.value?.items || []);
            }
        } catch (err) {
            if (isMounted.current) {
                const contractError = extractContractError(err, 'Failed to load analytics');
                setError(contractError.message);
            }
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, [timeRange, page, sortBy, sortDir, isMounted]);

    useEffect(() => {
        loadReporting();
    }, [loadReporting]);

    const handleExport = async (reportType = 'ad-unit') => {
        try {
            setIsExporting(true);
            const { from, to } = getTimeRangeDates(timeRange);
            const blob = await exportPublisherReport(reportType, from, to);
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bafkal-publisher-${reportType}-${from}-${to}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export CSV', err);
        } finally {
            setIsExporting(false);
        }
    };

    const handleAdUnitChange = (e) => {
        setSelectedAdUnit(e.target.value);
        setPage(1);
    };

    const activeSummary = useMemo(() => {
        if (selectedAdUnit === 'all') {
            return summary;
        }
        const unit = (adUnitReport?.items || []).find((item) => item.adUnitId === selectedAdUnit);
        if (!unit) {
            return {
                publisherNetMicrodollars: 0,
                impressionEcpm: null,
                requests: 0,
                fills: 0,
                impressions: 0,
                viewabilityRate: null,
                clicks: 0,
                fillRate: 0,
                ctr: 0,
                requestRpm: null,
            };
        }
        const requests = Number(unit.requests || 0);
        const fills = Number(unit.fills || 0);
        const impressions = Number(unit.impressions || 0);
        const viewable = Number(unit.viewableImpressions || 0);
        const clicks = Number(unit.clicks || 0);
        const netRevenue = Number(unit.publisherNetMicrodollars || 0);

        const fillRate = unit.fillRate != null ? unit.fillRate : (requests > 0 ? fills / requests : 0);
        const ctr = unit.ctr != null ? unit.ctr : (impressions > 0 ? clicks / impressions : 0);
        const viewabilityRate = impressions > 0 ? viewable / impressions : null;
        const impressionEcpm = unit.ecpm != null ? unit.ecpm : (impressions > 0 ? (netRevenue / impressions / 1000) : null);
        const requestRpm = requests > 0 ? (netRevenue / requests / 1000) : null;

        return {
            publisherNetMicrodollars: netRevenue,
            impressionEcpm,
            requests,
            fills,
            impressions,
            viewabilityRate,
            clicks,
            fillRate,
            ctr,
            requestRpm,
        };
    }, [selectedAdUnit, summary, adUnitReport]);

    const filteredRows = selectedAdUnit === 'all'
        ? (adUnitReport?.items || [])
        : (adUnitReport?.items || []).filter(item => item.adUnitId === selectedAdUnit);

    const PAGE_SIZE = 10;
    const paginatedRows = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredRows.slice(start, start + PAGE_SIZE);
    }, [filteredRows, page]);
    const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE) || 1;

    // Chart Data Preparation
    const timeSeriesData = {
        labels: dailyData.map(d => d.date),
        datasets: [
            {
                label: 'Requests',
                data: dailyData.map(d => d.requests || 0),
                borderColor: colors.neutral[400],
                backgroundColor: colors.neutral[100],
                fill: false,
                tension: 0.3,
            },
            {
                label: 'Fills',
                data: dailyData.map(d => d.fills || 0),
                borderColor: colors.primary[400],
                backgroundColor: colors.primary[50],
                fill: false,
                tension: 0.3,
            },
            {
                label: 'Impressions',
                data: dailyData.map(d => d.impressions || 0),
                borderColor: colors.primary[600],
                backgroundColor: colors.primary[100],
                fill: true,
                tension: 0.3,
            },
            {
                label: 'Clicks',
                data: dailyData.map(d => d.clicks || 0),
                borderColor: colors.success[500],
                backgroundColor: colors.success[50],
                fill: false,
                tension: 0.3,
            }
        ]
    };

    const revenueTrendData = {
        labels: dailyData.map(d => d.date),
        datasets: [{
            label: 'Net Revenue (USD)',
            data: dailyData.map(d => (d.publisherNetMicrodollars || 0) / 1000000),
            backgroundColor: colors.primary[600],
        }]
    };

    const formatDistributionData = {
        labels: formatData.length > 0 ? formatData.map(f => f.formatKey || f.adFamily || 'Other') : ['Display'],
        datasets: [{
            data: formatData.length > 0 ? formatData.map(f => f.impressions || f.requests || 0) : [summary?.impressions || 1],
            backgroundColor: [
                colors.primary[600],
                colors.success[500],
                colors.warning[500],
                colors.danger[500],
                colors.neutral[500],
            ],
            borderWidth: 1
        }]
    };

    const metricIcon = (svgPath, color) => (
        <svg style={{ width: '20px', height: '20px', color }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {svgPath}
        </svg>
    );

    // Table columns for Ad Unit Performance
    const adUnitColumns = [
        {
            header: 'Ad Unit',
            key: 'adUnitName',
            render: (name, row) => (
                <div>
                    <div className="font-medium text-gray-900">{name || 'Unnamed Unit'}</div>
                    <div className="text-xs text-gray-500 font-mono">{row.adUnitId}</div>
                </div>
            )
        },
        {
            header: 'Requests',
            key: 'requests',
            render: (val) => Number(val || 0).toLocaleString()
        },
        {
            header: 'Fills',
            key: 'fills',
            render: (val) => Number(val || 0).toLocaleString()
        },
        {
            header: 'Fill Rate',
            key: 'fillRate',
            render: (val) => formatPercent(val)
        },
        {
            header: 'Impressions',
            key: 'impressions',
            render: (val) => Number(val || 0).toLocaleString()
        },
        {
            header: 'Clicks',
            key: 'clicks',
            render: (val) => Number(val || 0).toLocaleString()
        },
        {
            header: 'CTR',
            key: 'ctr',
            render: (val) => `${(Number(val || 0) * 100).toFixed(2)}%`
        },
        {
            header: 'eCPM',
            key: 'ecpm',
            render: (val) => formatCurrency(val)
        },
        {
            header: 'Net Revenue',
            key: 'publisherNetMicrodollars',
            render: (val) => (
                <span className="font-semibold text-gray-900">
                    {formatMicrodollars(val)}
                </span>
            )
        }
    ];

    if (loading && !summary) {
        return (
            <div className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse space-y-4">
                        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                                <div key={n} className="bg-white p-6 rounded-lg shadow-sm h-32">
                                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                                    <div className="h-12 bg-gray-100 rounded"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!loading && !error && summary === null) {
        return (
            <div className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-2xl font-semibold text-gray-900 mb-4">Dashboard</h1>
                    <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <h3 className="mt-4 text-lg font-medium text-gray-900">Analytics not yet available</h3>
                        <p className="mt-2 text-sm text-gray-500">
                            Performance data will appear here once your ad units are live and serving impressions.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ErrorMessage message={error} />
                </div>
            </div>
        );
    }

    return (
        <div className="py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
                {/* Page header with filters & actions */}
                <div className="sm:flex sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Real-time advertising performance and monetization funnel analytics.
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-3">
                        <Select
                            value={selectedAdUnit}
                            onChange={handleAdUnitChange}
                        >
                            <option value="all">All Ad Units</option>
                            {adUnits.map(unit => (
                                <option key={unit.id} value={unit.id}>{unit.name}</option>
                            ))}
                        </Select>
                        <Select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                        >
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                        </Select>
                        <Button
                            variant="secondary"
                            size="md"
                            disabled={isExporting}
                            onClick={() => handleExport('ad-unit')}
                        >
                            {isExporting ? 'Exporting...' : 'Export CSV'}
                        </Button>
                    </div>
                </div>

                {/* Full-Funnel KPI Cards */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    <MetricCard
                        title="Net Revenue"
                        value={formatMicrodollars(activeSummary?.publisherNetMicrodollars)}
                        change={activeSummary?.impressionEcpm != null ? `$${activeSummary.impressionEcpm.toFixed(2)} eCPM` : null}
                        iconBg={colors.success[50]}
                        icon={metricIcon(
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
                            colors.success[600]
                        )}
                    />
                    <MetricCard
                        title="Ad Requests"
                        value={activeSummary?.requests != null ? Number(activeSummary.requests).toLocaleString() : '—'}
                        change={activeSummary?.fillRate != null ? `${(activeSummary.fillRate * 100).toFixed(1)}% Fill` : null}
                        iconBg={colors.primary[50]}
                        icon={metricIcon(
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />,
                            colors.primary[600]
                        )}
                    />
                    <MetricCard
                        title="Ad Fills"
                        value={activeSummary?.fills != null ? Number(activeSummary.fills).toLocaleString() : '—'}
                        change={activeSummary?.fills > 0 && activeSummary?.impressions != null ? `${((activeSummary.impressions / activeSummary.fills) * 100).toFixed(1)}% Render` : null}
                        iconBg={colors.primary[50]}
                        icon={metricIcon(
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
                            colors.primary[600]
                        )}
                    />
                    <MetricCard
                        title="Impressions"
                        value={activeSummary?.impressions != null ? Number(activeSummary.impressions).toLocaleString() : '—'}
                        change={activeSummary?.viewabilityRate != null ? `${(activeSummary.viewabilityRate * 100).toFixed(1)}% View` : null}
                        iconBg={colors.warning[50]}
                        icon={metricIcon(
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />,
                            colors.warning[600]
                        )}
                    />
                    <MetricCard
                        title="Clicks"
                        value={activeSummary?.clicks != null ? Number(activeSummary.clicks).toLocaleString() : '—'}
                        change={activeSummary?.ctr != null ? `${(activeSummary.ctr * 100).toFixed(2)}% CTR` : null}
                        iconBg={colors.success[50]}
                        icon={metricIcon(
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />,
                            colors.success[600]
                        )}
                    />
                    <MetricCard
                        title="Request RPM"
                        value={activeSummary?.requestRpm != null ? `$${activeSummary.requestRpm.toFixed(2)}` : '—'}
                        change="Yield / 1k Reqs"
                        iconBg={colors.neutral[100]}
                        icon={metricIcon(
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />,
                            colors.neutral[600]
                        )}
                    />
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <ChartContainer title="Funnel Delivery Over Time">
                            <div className="h-80">
                                <Line
                                    data={timeSeriesData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        interaction: { mode: 'index', intersect: false },
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                ticks: { callback: (value) => value.toLocaleString() }
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </ChartContainer>
                    </div>

                    <ChartContainer title="Format Breakdown">
                        <div className="h-80">
                            <Doughnut
                                data={formatDistributionData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { position: 'bottom' } }
                                }}
                            />
                        </div>
                    </ChartContainer>

                    <div className="lg:col-span-3">
                        <ChartContainer title="Daily Revenue Trends">
                            <div className="h-80">
                                <Bar
                                    data={revenueTrendData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                ticks: { callback: (value) => '$' + value.toLocaleString() }
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </ChartContainer>
                    </div>
                </div>

                {/* Ad Unit Breakdown Table */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                    <div className="sm:flex sm:items-center sm:justify-between mb-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Ad Unit Performance Breakdown</h3>
                            <p className="text-sm text-gray-500">Delivery, engagement, and revenue metrics by ad unit slot.</p>
                        </div>
                        <div className="mt-2 sm:mt-0 flex items-center gap-2">
                            <span className="text-xs text-gray-500">Sort By:</span>
                            <Select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="impressions">Impressions</option>
                                <option value="revenue">Revenue</option>
                                <option value="ecpm">eCPM</option>
                                <option value="ctr">CTR</option>
                                <option value="fillRate">Fill Rate</option>
                            </Select>
                            <Select
                                value={sortDir}
                                onChange={(e) => setSortDir(e.target.value)}
                            >
                                <option value="desc">Descending</option>
                                <option value="asc">Ascending</option>
                            </Select>
                        </div>
                    </div>

                    <Table
                        columns={adUnitColumns}
                        data={paginatedRows}
                        keyField="adUnitId"
                        emptyMessage="No ad unit performance recorded for this date range."
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
                            <div className="text-sm text-gray-700">
                                Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page <= 1}
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;